import React, { useEffect, useState } from 'react';

import { LogIn, Settings, User } from 'lucide-react';
import Link from '@/components/ui/link';
import { useLocation } from 'react-router';

import { useSession } from '@/lib/auth-client';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { resolveMobileNavItems } from '@/config/mobile-nav';

interface MobileBottomNavItem {
  name: string;
  href?: string;
  icon: any;
  onClick?: () => void;
}

interface ProfileDropdownProps {
  isVisible: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
}

function ProfileDropdown({
  isVisible,
  onClose,
  isLoggedIn,
  user,
}: ProfileDropdownProps): React.ReactElement | null {
  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-24 right-3 z-50 w-[min(16rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border/60 bg-background/65 shadow-lg backdrop-blur-xl md:hidden"
      data-profile-dropdown
    >
      <div className="flex items-center gap-3 border-b border-border/60 bg-muted/20 px-3 py-3">
        <Avatar className="h-9 w-9 border border-border/60">
          <AvatarImage
            src={
              user.image ||
              'https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg'
            }
          />
          <AvatarFallback>
            <User />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-foreground">
            {user.name || 'Guest'}
          </span>
          <span className="text-xs text-muted-foreground">
            {user.email || 'Not signed in'}
          </span>
        </div>
      </div>
      <div className="space-y-0.5 p-2">
        {isLoggedIn ? (
          <>
            <Link
              href={
                user.name
                  ? `/@${encodeURIComponent(user.name)}`
                  : '/settings/profile'
              }
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={onClose}
            >
              <User className="h-4 w-4" />
              <span>My Profile</span>
            </Link>
            <Link
              href="/settings/profile"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={onClose}
            >
              <User className="h-4 w-4" />
              <span>Account</span>
            </Link>
          </>
        ) : (
          <Link
            href="/auth/sign-in"
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            onClick={onClose}
          >
            <LogIn className="h-4 w-4" />
            <span>Log In</span>
          </Link>
        )}
        <Link
          href="/settings"
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          onClick={onClose}
        >
          <Settings className="h-4 w-4" />
          <span>Settings</span>
        </Link>
        {isLoggedIn ? (
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-destructive transition-colors hover:text-destructive/80"
            onClick={onClose}
          >
            <span>Log Out</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface NavItemProps {
  item: MobileBottomNavItem;
  isActive: boolean;
  session: any;
}

function NavItem({
  item,
  isActive,
  session,
}: NavItemProps): React.ReactElement {
  const Icon = item.icon;
  const isProfile = item.name === 'Profile';
  const iconClassName = 'h-4 w-4';

  const handleItemClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (isProfile && item.onClick) {
      e.preventDefault();
      item.onClick();
    }
  };

  if (isProfile) {
    return (
      <button
        key={item.name}
        type="button"
        onClick={handleItemClick}
        className="group relative flex h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-muted-foreground transition-colors duration-150 ease-in-out hover:text-foreground active:scale-[0.98]"
        aria-label={item.name}
        data-profile-button
      >
        {session.data?.user.id ? (
          <Avatar className="h-5 w-5">
            <AvatarImage
              src={
                session.data.user.avatarUrl ||
                'https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg'
              }
            />
            <AvatarFallback>
              <User className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
        ) : (
          <Icon
            className={`${iconClassName} transition-transform duration-150 ease-in-out group-active:scale-90`}
          />
        )}
        <span className="text-[10px] font-medium leading-none">Profile</span>
      </button>
    );
  }

  return (
    <Link
      key={item.href}
      href={item.href as string}
      className={`group relative flex h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 transition-colors duration-150 ease-in-out ${isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground active:scale-[0.98]'}`}
      aria-label={item.name}
    >
      <Icon
        className={`${iconClassName} transition-transform duration-150 ease-in-out group-active:scale-90`}
      />
      <span className="text-[10px] font-medium leading-none">{item.name}</span>
    </Link>
  );
}

export default function MobileBottomNav(): React.ReactElement | null {
  const pathname = useLocation().pathname;
  const session = useSession();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const mobileNavItems: MobileBottomNavItem[] = resolveMobileNavItems(
    session,
    () => {
      setShowProfileDropdown((v) => !v);
    }
  );

  useEffect(() => {
    if (!showProfileDropdown) {
      return () => {};
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const clickedDropdown = !!target.closest('[data-profile-dropdown]');
      const clickedButton = !!target.closest('[data-profile-button]');

      if (!clickedDropdown && !clickedButton) {
        setShowProfileDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showProfileDropdown]);

  if (!pathname) return null;

  return (
    <>
      <nav className="fixed bottom-3 left-3 right-3 z-40 bg-transparent md:hidden">
        <div
          className="mx-auto flex max-w-md items-stretch justify-around gap-1 rounded-2xl border border-border/60 bg-background/55 p-1 shadow-lg backdrop-blur-xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {mobileNavItems.map((item) => (
            <NavItem
              key={item.href || item.name}
              item={item}
              isActive={pathname === item.href}
              session={session}
            />
          ))}
        </div>
      </nav>
      <ProfileDropdown
        isVisible={showProfileDropdown}
        onClose={() => setShowProfileDropdown(false)}
        isLoggedIn={!!session.data?.user.id}
        user={session.data?.user || {}}
      />
    </>
  );
}
